/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-const */
import { Editor, Extension } from "@tiptap/core"
import { EditorState, Plugin } from "@tiptap/pm/state"
import { EditorView } from "@tiptap/pm/view"
import {
  dragClipboardDegradedSlice,
  dragClipboardSlice,
  getDragClipboard,
} from "../../util/clipboard"
import {
  classifySlice,
  findNearestDropPos,
  isLessonDrag,
  posAtEvent,
} from "../../util/drag-and-drop"

// Modified from https://github.com/ueberdosis/tiptap/blob/main/packages/extensions/src/drop-cursor/drop-cursor.ts
// and https://github.com/ProseMirror/prosemirror-dropcursor/blob/master/src/dropcursor.ts
// which are MIT licensed.

export interface CpDropcursorOptions {
  /**
   * The width of the drop cursor
   * @default 1
   * @example 2
   */
  width: number | undefined

  /**
   * The class of the drop cursor. All styling (including color) comes from this class —
   * Cp mod: the upstream `color` option was removed since we style via CSS only.
   * @default undefined
   * @example 'drop-cursor'
   */
  class: string | undefined
}

/**
 * This extension allows you to add a drop cursor to your editor.
 * A drop cursor is a line that appears when you drag and drop content
 * in-between nodes.
 *
 * Forked for Common Planner (including forking prosemirror-dropcursor)
 * to show the drop cursor at our own drop position (computed by findNearestDropPos).
 *
 * @see https://tiptap.dev/api/extensions/dropcursor
 */
export const CpDropcursor = Extension.create<CpDropcursorOptions>({
  name: "dropCursor",

  addOptions() {
    return {
      width: 1,
      class: undefined,
    }
  },

  addProseMirrorPlugins() {
    return [dropCursor(this.editor, this.options)]
  },
})

interface DropCursorOptions {
  /// The precise width of the cursor in pixels. Defaults to 1.
  width?: number

  /// A CSS class name to add to the cursor element.
  class?: string
}

/**
 * Node types whose DOM draws a bottom-separator border (`border-bottom` in
 * card.module.css — keep in sync with those styles). The drop
 * indicator sits on the separator instead of centering in the gap below them.
 */
const NODE_TYPES_WITH_BOTTOM_SEPARATOR = new Set([
  "titledCard",
  "untitledCard",
  "cardGoogleClassroom",
])

/// Create a plugin that, when added to a ProseMirror instance,
/// causes a decoration to show up at the drop position when something
/// is dragged over the editor.
function dropCursor(editor: Editor, options: DropCursorOptions = {}): Plugin {
  return new Plugin({
    view(editorView) {
      return new DropCursorView(editor, editorView, options)
    },
  })
}

class DropCursorView {
  width: number
  class: string | undefined
  cursorPos: number | null = null
  element: HTMLElement | null = null
  timeout: number = -1
  handlers: { name: string; handler: (event: Event) => void }[]

  constructor(
    // Cp mod: Store Tiptap editor.
    readonly editor: Editor,
    readonly editorView: EditorView,
    options: DropCursorOptions,
  ) {
    this.width = options.width ?? 1
    this.class = options.class

    this.handlers = ["dragover", "dragend", "drop", "dragleave"].map((name) => {
      let handler = (e: Event) => {
        ;(this as any)[name](e)
      }
      editorView.dom.addEventListener(name, handler)
      return { name, handler }
    })
  }

  destroy() {
    this.handlers.forEach(({ name, handler }) =>
      this.editorView.dom.removeEventListener(name, handler),
    )
  }

  update(editorView: EditorView, prevState: EditorState) {
    // TODO: Recompute from mouse pos, or use an ElementId?
    if (this.cursorPos != null && prevState.doc != editorView.state.doc) {
      if (this.cursorPos > editorView.state.doc.content.size) this.setCursor(null)
      else this.updateOverlay()
    }
  }

  setCursor(pos: number | null) {
    if (pos == this.cursorPos) return
    this.cursorPos = pos
    if (pos == null) {
      this.element!.parentNode!.removeChild(this.element!)
      this.element = null
    } else {
      this.updateOverlay()
    }
  }

  updateOverlay() {
    let $pos = this.editorView.state.doc.resolve(this.cursorPos!)
    let isBlock = !$pos.parent.inlineContent,
      rect
    let editorDOM = this.editorView.dom,
      editorRect = editorDOM.getBoundingClientRect()
    let scaleX = editorRect.width / editorDOM.offsetWidth,
      scaleY = editorRect.height / editorDOM.offsetHeight
    if (isBlock) {
      let before = $pos.nodeBefore,
        after = $pos.nodeAfter
      if (before || after) {
        let node = this.editorView.nodeDOM(this.cursorPos! - (before ? before.nodeSize : 0))
        if (node) {
          let nodeRect = (node as HTMLElement).getBoundingClientRect()
          let cardRect = (node as HTMLElement)
            .querySelector<HTMLElement>(".unified-tiptap-card-separator")
            ?.getBoundingClientRect()
          let top = before ? nodeRect.bottom : nodeRect.top
          // Cp mod: don't center in the gap when the node above draws its own
          // bottom-separator border — keep the line at that node's bottom edge so it
          // overlaps the separator instead of drawing a second line just below it.
          if (before && after && !NODE_TYPES_WITH_BOTTOM_SEPARATOR.has(before.type.name))
            top =
              (top +
                (this.editorView.nodeDOM(this.cursorPos!) as HTMLElement).getBoundingClientRect()
                  .top) /
              2
          let halfWidth = (this.width / 2) * scaleY
          rect = {
            left: cardRect?.left ?? nodeRect.left,
            right: cardRect?.right ?? nodeRect.right,
            top: top - halfWidth,
            bottom: top + halfWidth,
          }
        }
      }
    }
    if (!rect) {
      let coords = this.editorView.coordsAtPos(this.cursorPos!)
      let halfWidth = (this.width / 2) * scaleX
      rect = {
        left: coords.left - halfWidth,
        right: coords.left + halfWidth,
        top: coords.top,
        bottom: coords.bottom,
      }
    }

    let parent = this.editorView.dom.offsetParent as HTMLElement
    if (!this.element) {
      this.element = parent.appendChild(document.createElement("div"))
      if (this.class) this.element.className = this.class
      this.element.style.cssText = "position: absolute; z-index: 50; pointer-events: none;"
    }
    this.element.classList.toggle("prosemirror-dropcursor-block", isBlock)
    this.element.classList.toggle("prosemirror-dropcursor-inline", !isBlock)
    let parentLeft, parentTop
    if (!parent || (parent == document.body && getComputedStyle(parent).position == "static")) {
      parentLeft = -pageXOffset
      parentTop = -pageYOffset
    } else {
      let rect = parent.getBoundingClientRect()
      let parentScaleX = rect.width / parent.offsetWidth,
        parentScaleY = rect.height / parent.offsetHeight
      parentLeft = rect.left - parent.scrollLeft * parentScaleX
      parentTop = rect.top - parent.scrollTop * parentScaleY
    }
    this.element.style.left = (rect.left - parentLeft) / scaleX + "px"
    this.element.style.top = (rect.top - parentTop) / scaleY + "px"
    this.element.style.width = (rect.right - rect.left) / scaleX + "px"
    this.element.style.height = (rect.bottom - rect.top) / scaleY + "px"
  }

  scheduleRemoval(timeout: number) {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.setCursor(null), timeout) as unknown as number
  }

  // Cp mod: Redo this method to behave similarly to our handleSliceDrop.
  dragover(event: DragEvent) {
    if (!this.editorView.editable) return
    // A whole-lesson drag drops on the lesson SLOT, never into an editor — no cursor.
    if (isLessonDrag(event)) return

    const dragClipboard = getDragClipboard()
    if (dragClipboard) {
      // A schema that can't hold the content as-is (a card over a note editor) still
      // accepts its degraded parse — what the drop will actually insert — so the cursor
      // paints exactly when releasing would land something, and nowhere it wouldn't.
      const exact = dragClipboardSlice(dragClipboard, this.editorView.state.schema)
      const slice = exact ?? dragClipboardDegradedSlice(dragClipboard, this.editorView.state.schema)
      if (slice.content.size === 0) return
      const sliceType = exact !== null ? dragClipboard.sliceType : classifySlice(slice)
      const dropPos = findNearestDropPos(this.editor, event, sliceType, () => slice)
      if (dropPos !== null) {
        this.setCursor(dropPos)
        this.scheduleRemoval(5000)
      }
    } else {
      // Content dragged from outside our app.
      // We don't know what the Slice is because HTML DnD doesn't allow dataTransfer access in dragover.
      // As in the original code, use the exact mouse pos.
      const pos = posAtEvent(this.editorView, event)
      if (pos !== null) {
        this.setCursor(pos)
        this.scheduleRemoval(5000)
      }
    }
  }

  dragend() {
    this.scheduleRemoval(20)
  }

  drop() {
    this.scheduleRemoval(20)
  }

  dragleave(event: DragEvent) {
    if (!this.editorView.dom.contains((event as any).relatedTarget)) this.setCursor(null)
  }
}
